import React, { useState } from "react";
import { FilterItem } from "./FilterItem";
import PropTypes from "prop-types";

import styles from "./filter.module.scss";
import cx from "classnames";
import { RadioButton, RadioButtonGroup, Tooltip } from "@carbon/react";
import useUser from "@/lib/hooks/useUser";

const get = (value, path, defaultValue) => {
  return String(path)
    .split(".")
    .reduce((acc, v) => {
      try {
        acc = acc[v];
      } catch (e) {
        return defaultValue;
      }
      return acc;
    }, value);
};

const SideFilter = ({
  onChange = (filter) => {
    console.info("filter change called for ", filter);
  },
  onChangeOrder = (order) => {
    console.info("order change called for ", order);
  },
  filters = [],
  orders = [],
  orderTitle = "Sortieren nach",
  orderHelpText = "Sortieren nach",
  title = "Filter",
  titleHelpText = "Der Filter hilft dir den Überblick zu bewahren und zeigt dir nur die Medien an, die du sehen möchtest.",
  valueField = "value",
  // Slot rendered inside the styled container, above the regular filter
  // section. Lets callers add their own controls (e.g. a customer-scope
  // ComboBox) that share the same white-box look without forcing every such
  // case into the generic `filters` array.
  prependSlot = null,
}) => {
  const { isAdmin } = useUser();
  const [active, setActive] = useState(false);
  const [selected, setSelected] = useState({});
  const [activeOrder, setActiveOrder] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState({});

  const resetFilter = (e) => {
    e.preventDefault();
    setActive(false);
    setSelected({});
    onChange({});
  };

  const resetOrder = (e) => {
    e.preventDefault();
    setActiveOrder(false);
    setSelectedOrder({});
    onChange({});
  };

  const changeFilter = (id, state, type, displayAs = "checkbox") => {
    let currentSelection = selected || {};
    let forType = currentSelection[type] || [];

    if (displayAs === "radiobox") {
      forType = [id];
    } else {
      if (state === true) {
        if (!forType.includes(id)) {
          forType = [...forType, id];
        }
      } else {
        if (forType.includes(id)) {
          forType = forType.filter((item) => item !== id);
        }
      }
    }

    if (forType.length < 1) {
      delete currentSelection[type];
    } else {
      currentSelection[type] = forType;
    }

    setSelected(currentSelection);
    setActive(Object.keys(currentSelection).length > 0);
    onChange(currentSelection);
  };

  const box_is_selected = (id, type) => {
    let forType = selected[type] || [];

    if (!forType.includes(id)) {
      return false;
    } else {
      return true;
    }
  };

  return (
    <div className={cx(styles.container)}>
      {prependSlot && (
        <fieldset className="cds--fieldset" style={{ marginBottom: "1rem", paddingRight: "1rem" }}>
          {prependSlot}
        </fieldset>
      )}
      {orders.length > 0 && (
        <>
          <div className={cx(styles.header)}>
            <div className={cx(styles.header_title)}>
              <Tooltip align="top-left" direction="top" label={titleHelpText}>
                <span>{orderTitle}</span>
              </Tooltip>
            </div>
            {active && (
              <button
                href="#"
                className={cx("cds--link", styles.filter_action)}
                onClick={resetOrder}
              >
                zurücksetzen
              </button>
            )}
          </div>

          <section className={cx(styles.filter_options)}>
            {filters.map((filter) => {
              return (
                <fieldset className="cds--fieldset" key={`fieldset_filter_item:${filter.title}`}>
                  <legend className="cds--label">{filter.title}</legend>

                  {filter.displayAs === "radiobox" ? (
                    <RadioButtonGroup
                      orientation={"vertical"}
                      name={filter.type}
                      defaultSelected={`${get(filter.items[0], valueField, "")}`}
                      valueSelected={filter.value}
                      onChange={(e) => {
                        changeFilter(e, true, filter.type, filter.displayAs);
                      }}
                    >
                      {filter.items.map((item) => (
                        <RadioButton
                          className={cx(styles.item_inner)}
                          id={`${get(item, valueField, null)}`}
                          labelText={item.label}
                          key={`filter_item|${item.label}|${get(item, valueField, null)}`}
                          value={`${get(item, valueField, null)}`}
                        ></RadioButton>
                      ))}
                    </RadioButtonGroup>
                  ) : (
                    filter.items.map((item) => (
                      <FilterItem
                        type={filter.type}
                        name={filter.title}
                        label={item.label}
                        id={`${get(item, valueField, null)}`}
                        rightText={`${item.count || 0}`}
                        key={`filter_item:${item.label}:${get(item, valueField, null)}`}
                        onChange={(id, state) => {
                          let parsedId = parseInt(id);
                          changeFilter(parsedId || id, state, filter.type, "checkbox");
                        }}
                        checked={box_is_selected(get(item, valueField, null), filter.type)}
                      />
                    ))
                  )}
                </fieldset>
              );
            })}
          </section>
        </>
      )}
      <div className={cx(styles.header)}>
        <div className={cx(styles.header_title)}>
          <Tooltip align="top-left" direction="top" label={titleHelpText}>
            <span>{title}</span>
          </Tooltip>
        </div>
        {active && (
          <button href="#" className={cx("cds--link", styles.filter_action)} onClick={resetFilter}>
            zurücksetzen
          </button>
        )}
      </div>

      <section className={cx(styles.filter_options)}>
        {filters
          .filter((f) => !f.onlyAdmin || (f.onlyAdmin && isAdmin))
          .map((filter) => {
            // Number of active selections for this filter section. Shown next
            // to the title so operators can see at a glance which collapsed
            // sections have active filters.
            const activeCount = (selected[filter.type] || []).length;
            return (
              <details className={cx(styles.filter_section)} key={`fieldset_filter_item:${filter.title}`}>
                <summary className={cx(styles.filter_summary)}>
                  <span className={cx(styles.filter_summary_label)}>{filter.title}</span>
                  {activeCount > 0 && (
                    <span className={cx(styles.filter_summary_badge)}>{activeCount}</span>
                  )}
                </summary>
                <div className={cx(styles.filter_section_body)}>
                  {filter.displayAs === "radiobox" ? (
                    <RadioButtonGroup
                      orientation={"vertical"}
                      name={filter.type}
                      defaultSelected={`${get(filter.items[0], valueField, "")}`}
                      valueSelected={filter.value}
                      onChange={(e) => {
                        changeFilter(e, true, filter.type, filter.displayAs);
                      }}
                    >
                      {filter.items.map((item) => (
                        <RadioButton
                          className={cx(styles.item_inner)}
                          id={`${get(item, valueField, null)}`}
                          labelText={item.label}
                          key={`filter_item|${item.label}|${get(item, valueField, null)}`}
                          value={`${get(item, valueField, null)}`}
                        ></RadioButton>
                      ))}
                    </RadioButtonGroup>
                  ) : (
                    filter.items.map((item) => (
                      <FilterItem
                        type={filter.type}
                        name={filter.title}
                        label={item.label}
                        id={`${get(item, valueField, null)}`}
                        disableCount={filter.disableCount}
                        rightText={`${item.count || 0}`}
                        key={`filter_item:${item.label}:${get(item, valueField, null)}`}
                        onChange={(id, state) => {
                          let parsedId = parseInt(id);
                          changeFilter(parsedId || id, state, filter.type, "checkbox");
                        }}
                        checked={box_is_selected(get(item, valueField, null), filter.type)}
                      />
                    ))
                  )}
                </div>
              </details>
            );
          })}
      </section>
    </div>
  );
};

SideFilter.propTypes = {
  filters: PropTypes.array,
  title: PropTypes.string,
  valueField: PropTypes.string,
  onChange: PropTypes.func,
  orders: PropTypes.array,
  orderTitle: PropTypes.string,
  orderHelpText: PropTypes.string,
  onChangeOrder: PropTypes.func,
};

export default SideFilter;
